import { Component } from 'react'
import { Helmet } from 'react-helmet'
import { appConnect } from '~/store/hooks'
import { Line } from 'rc-progress'
import * as filestack from 'filestack-js'
import get from 'lodash-es/get'
import isEmpty from 'lodash-es/isEmpty'
import PropTypes from 'prop-types'

import routes from 'config/routes'
import { getTitle } from 'config/titles'
import theme from 'config/theme'
import { addBackgroundImage } from 'store/objects'
import FILESTACK_API_KEY from 'config/filestack'
import LAYER_KEYS from 'config/layerKeys'

import Space from 'components/UIKit/Space'
import Alert from 'components/UIKit/Alert'
import Container from './styled/Container'

import { Modal, MODAL_ANIMATION_VARIANT } from '~/ui/Modal'
import { Dialog } from '~/ui/Dialog'
import { Heading } from 'react-aria-components'
import { withNavigate } from '~/utils/reactRouterHOC'
import { withApollo } from '@apollo/client/react/hoc'
import { GET_SIGNED_CONVERTED_URL } from '~/client/queries/getSignedConvertedURL'

/**
 * @typedef {object} Props
 * @prop {any} addBackgroundImage
 * @prop {any} backgroundImageForm
 * @prop {any} navigate
 * @prop {any} props
 * @prop {import('@apollo/client').ApolloClient} client
 *
 * @extends {Component<Props>}
 */
class ConvertImageModal extends Component {
  state = {
    isProcessing: true,
    percent: 0,
    message: 'Preparing file…',
    error: '',
    isOpen: true,
  }

  static propTypes = {
    addBackgroundImage: PropTypes.func,
    backgroundImageForm: PropTypes.object,
    navigate: PropTypes.func,
    props: PropTypes.object,
    client: PropTypes.object,
  }

  componentDidMount() {
    this.client = filestack.init(FILESTACK_API_KEY)

    if (!isEmpty(this.props.backgroundImageForm)) {
      this.handleCreateJob()
    }
  }

  componentDidUpdate(prevProps) {
    if (isEmpty(prevProps.backgroundImageForm) && !isEmpty(this.props.backgroundImageForm)) {
      this.handleCreateJob()
    }
  }

  handleCreateJob = () => {
    const { backgroundImageForm, client } = this.props
    const filename = get(this.props, 'backgroundImageForm.filename')
    if (isEmpty(filename)) {
      this.setState({
        error: 'Nothing to convert',
        isProcessing: false,
      })
      return
    }

    client
      .query({
        query: GET_SIGNED_CONVERTED_URL,
        variables: {
          url: backgroundImageForm.src,
          filename: filename,
        },
      })
      .then(async ({ data }) => {
        const url = data.url.url
        const response = await fetch(url)
        if (!response.ok) {
          this.setState({
            isProcessing: false,
            error: 'Conversion error! Please submit a ticket.',
          })
          return
        }

        this.setState(
          {
            percent: 100,
            message: 'Conversion complete!',
            fileUrl: url,
          },
          () => {
            this.handleUpload()
          }
        )
      })
  }

  handleUpload = () => {
    const { fileUrl } = this.state

    if (isEmpty(fileUrl)) {
      this.setState({
        error: 'Nothing to upload',
        isProcessing: false,
      })
      return
    }

    this.client
      .storeURL(fileUrl)
      .then(file => {
        this.client
          .metadata(file.handle, { width: true, height: true })
          .then(res => {
            this.handleSave({
              src: file.url,
              mimetype: file.mimetype,
              width: res.width,
              height: res.height,
              filename: file.filename,
            })
          })
          .catch(error => {
            this.setState({ error: error.message, isProcessing: false })
          })
      })
      .catch(error => {
        this.setState({ error: error.message, isProcessing: false })
      })
  }

  handleSave = ({ src, width, height }) => {
    this.props.addBackgroundImage({
      backgroundImage: {
        BACKGROUND_IMAGE: {
          src,
          originalWidth: width,
          originalHeight: height,
          width,
          height,
          opacity: 1.0,
          position: {
            x: 0,
            y: 0,
            z: 0,
          },
          rotation: {
            x: 0,
            y: 0,
            z: 0,
          },
          layerKey: LAYER_KEYS.BACKGROUND_IMAGE,
          id: LAYER_KEYS.BACKGROUND_IMAGE,
        },
      },
    })

    this.props.navigate(`..${routes.modals.editImage}`, { relative: 'path' })
  }

  render() {
    const { isProcessing, error } = this.state
    return (
      <div>
        <Helmet>
          <title>{getTitle('convertImage')}</title>
        </Helmet>
        <Modal
          isDismissable
          isOpen={this.state.isOpen}
          onOpenChange={open => this.setState({ isOpen: open })}
          onAnimationComplete={animation => {
            if (animation === MODAL_ANIMATION_VARIANT.hidden) this.props.navigate('..', { relative: 'path' })
          }}
          className="max-w-3xl"
        >
          <Dialog>
            <Heading slot="title" className="text-lg pb-3 mb-3 border-b">
              Convert Image
            </Heading>
            <Container>
              {error.length > 0 && (
                <Space bottom="base">
                  <Alert type="error" text={error} />
                </Space>
              )}
              {isProcessing && (
                <>
                  <Space bottom="base">
                    <p>
                      {parseInt(this.state.percent, 10)}% - {this.state.message}
                    </p>
                  </Space>
                  <Line percent={parseInt(this.state.percent, 10)} strokeColor={theme.colors.dark.primary} />
                </>
              )}
            </Container>
          </Dialog>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = ({ backgroundImageForm }) => ({
  backgroundImageForm,
})

const mapDispatchToProps = { addBackgroundImage }

export default appConnect(mapStateToProps, mapDispatchToProps)(withNavigate(withApollo(ConvertImageModal)))
